import { inject } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot, Router } from '@angular/router';
import { catchError, map, switchMap, EMPTY } from 'rxjs';
import { BookingService } from './booking.service';
import { BrandContextService } from '../../core/services/brand-context.service';
import { BookingInvitation, BookingSlot } from './booking.model';

export interface BookingResolvedData {
  invitationId: string;
  invitation: BookingInvitation;
  slots: BookingSlot[];
}

export const bookingResolver: ResolveFn<BookingResolvedData> = (route: ActivatedRouteSnapshot) => {
  const bookingService = inject(BookingService);
  const brandContext = inject(BrandContextService);
  const router = inject(Router);

  const invitationId = route.paramMap.get('invitationId') ?? '';

  if (!invitationId) {
    router.navigate(['/invalid']);
    return EMPTY;
  }

  const now = new Date();
  const fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
  const toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

  // Fetch the invitation first to obtain brandId and check lifecycle state.
  // Only Pending (1) invitations may proceed to slot fetching.
  return bookingService.getInvitation(invitationId).pipe(
    switchMap(invitation => {
      brandContext.brandId.set(invitation.brandId);

      if (invitation.state === 2) {
        router.navigate(['book', invitationId, 'completed']);
        return EMPTY;
      }
      if (invitation.state === 3) {
        router.navigate(['book', invitationId, 'expired']);
        return EMPTY;
      }

      return bookingService.getAvailableSlots(invitationId, fromDate, toDate).pipe(
        map(slotsResponse => ({
          invitationId,
          invitation,
          slots: slotsResponse.days.flatMap(day =>
            day.slots.map(slot => ({
              id: slot.id,
              startDateTime: slot.startDateTime,
              endDateTime: slot.endDateTime,
              label: slot.label,
            }))
          ),
        })),
      );
    }),
    catchError(() => {
      router.navigate(['/invalid']);
      return EMPTY;
    }),
  );
};
